import {
  FieldAgreement,
  MatchVerdictDecision,
  type AcceptedRecordDto,
  type PossibleMatchDto,
  type ProspectDto,
  type RecordMatchVerdictRequest,
  type UpdateProspectRequest,
} from '@signalvote/shared';
import { titleCase } from './matchVerdict';

/** The non-blank parts, joined. */
export const joined = (separator: string, ...parts: (string | null | undefined)[]) =>
  parts.filter((part): part is string => !!part && part.trim() !== '').join(separator);

type AddressParts = Pick<ProspectDto, 'address' | 'addressLine2' | 'city' | 'state' | 'zipCode'>;

/** "3021 7th Avenue South, Birmingham, AL 35233" — a Prospect's address as one line. */
export const prospectAddress = (p: AddressParts) =>
  joined(', ', p.address, p.addressLine2, p.city, joined(' ', p.state, p.zipCode));

/** A federal record's address as one line, in the same shape as the Prospect's. */
export const recordAddress = (r: PossibleMatchDto) =>
  joined(', ', titleCase(r.streetFolded), titleCase(r.city), joined(' ', r.state, r.zip5));

/** "Brasfield & Gorrie · Chairman" — a federal record's employer and occupation. */
export const recordWork = (r: PossibleMatchDto) => joined(' · ', titleCase(r.employer), titleCase(r.occupation));

/**
 * The Prospect already holds this record's address: the server says so, or "Use address" would write exactly the
 * line they have (the server calls two street-less addresses only close, yet using one would change nothing).
 */
export const addressInUse = (r: PossibleMatchDto, p: ProspectDto) =>
  r.agreement.address === FieldAgreement.Same ||
  prospectAddress(addressFromRecord(p, r)).toLowerCase() === prospectAddress(p).toLowerCase();

/** The Prospect already holds this record's employer and occupation (a blank side of the record doesn't count against it). */
export const workInUse = (r: PossibleMatchDto) =>
  (r.employer.trim() === '' || r.agreement.employer === FieldAgreement.Same) &&
  (r.occupation.trim() === '' || r.agreement.occupation === FieldAgreement.Same);

/** The prospect PUT for every detail as it stands, with some changed — the PUT replaces them all. */
function withChanges(p: ProspectDto, changes: Partial<UpdateProspectRequest>): UpdateProspectRequest {
  const { id: _id, name: _name, notes: _notes, ...details } = p;
  return { ...details, ...changes };
}

/** A ZIP's five-digit part — "35203" from "35203-1234" or "352031234" — or "" when it doesn't start with five digits. */
const zip5Of = (zip: string | null) => /^\d{5}/.exec(zip?.trim() ?? '')?.[0] ?? '';

/**
 * "Use address": the record's street, city, state and ZIP in place of theirs (its address has no unit line). The
 * record only knows five digits, so their ZIP+4 stays when its five digits are the record's.
 */
export function addressFromRecord(p: ProspectDto, r: PossibleMatchDto): UpdateProspectRequest {
  const sameZip = r.zip5 !== '' && zip5Of(p.zipCode) === r.zip5;
  return withChanges(p, {
    address: titleCase(r.streetFolded),
    addressLine2: null,
    city: titleCase(r.city) || null,
    state: r.state || null,
    zipCode: sameZip ? p.zipCode : r.zip5 || null,
  });
}

/** "Use this": the record's employer and occupation in place of theirs; a blank on the record keeps theirs. */
export const workFromRecord = (p: ProspectDto, r: PossibleMatchDto): UpdateProspectRequest =>
  withChanges(p, {
    ...(r.employer.trim() ? { employer: titleCase(r.employer) } : {}),
    ...(r.occupation.trim() ? { occupation: titleCase(r.occupation) } : {}),
  });

/**
 * "Unlink": a "not them" answer about an accepted record. An automatic accept has no verdict yet, so it's a
 * first answer with no token; a confirmed one changes its verdict and sends that verdict's token.
 */
export const unlinkVerdict = ({ record: r, verdictConcurrencyVersion }: AcceptedRecordDto): RecordMatchVerdictRequest => ({
  givenName: r.givenName,
  lastCore: r.lastCore,
  suffix: r.suffix,
  streetFolded: r.streetFolded,
  zip5: r.zip5,
  decision: MatchVerdictDecision.Reject,
  concurrencyVersion: verdictConcurrencyVersion,
});
